import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import secret from "../secret.json";
import { authService } from "../services/authService";

import TopNavBar from "../components/TopNavBar";
import ExperiencePromoModal from "../components/ExperiencePromoModal";
import WorksheetTable from "../features/worksheets/components/WorksheetTable.jsx";
import StudentManagementPage from "../features/student/components/StudentManagementPage";
import AcademyManagementPage from "../features/academy/components/AcademyManagementPage.jsx";
import MonthlySummaryPopup from "../features/academy/components/MonthlySummaryPopup.jsx";
import MonthlyReportPage from "../features/academy/components/MonthlyReportPage.jsx";
import ErrorReportPage from "../features/support/components/ErrorReportPage.jsx";
import AdminErrorReportPage from "../features/support/components/AdminErrorReportPage.jsx";
import FixNoticePopup from "../features/support/components/FixNoticePopup.jsx";
import CapacityOverPopup from "../features/academy/components/CapacityOverPopup.jsx";
import MonthlySummaryViewer from "../features/academy/components/MonthlySummaryViewer.jsx";
import AssignedWorksheetTable from "../features/worksheets/components/AssignedWorksheetTable/AssignedWorksheetTable.jsx";
import UnfinishedWorksheetTable from "../features/worksheets/components/UnfinishedWorksheetTable.jsx";
import AssignmentReviewTable from "../features/worksheets/components/AssignmentReviewTable.jsx";
import ProblemBankPage from "../features/problem-bank/ProblemBankPage";
import LevelTestManagement from "../features/leveltest/components/LevelTestManagement";
import LevelTestCodeVault from "../features/leveltest/components/LevelTestCodeVault";
import { fetchLevelTestEntitlementAPI } from "../features/leveltest/levelTestAPI";
import { savePostLoginRedirect } from "../services/postLoginRedirect";
import AssignedRetryWorksheetTable from "../features/worksheets/components/AssignedRetryWorksheetTable.jsx";
import AdminPage from "../features/admin/components/AdminPage.jsx";
import AdminLevelTestPage from "../features/admin/components/AdminLevelTestPage";
import PartnerAcademyPage from "../features/admin/components/PartnerAcademyPage";
import GrammarBankPage from "../features/grammar-bank/components/GrammarBankPage.jsx";
import NotificationManagementPage from "../features/notification/components/NotificationManagementPage.jsx";
import PaperAssignmentPage from "../features/paper-assignment/components/PaperAssignmentPage.jsx";

// 메뉴 구조가 그대로 주소가 된다. /<메뉴>/<하위 메뉴>
const BASE_TAB_LIST = [
  {
    name: "worksheet",
    text: "문제지 관리",
    subTabList: [
      {
        name: "list",
        text: "문제지 목록",
      },
      {
        name: "given",
        text: "출제 문제지 목록",
      },
      {
        name: "retry",
        text: "복습 문제지 목록",
      },
      {
        name: "leveltest",
        text: "레벨테스트 관리",
      },
      {
        name: "bank",
        text: "문제뱅크",
        badge: "special",   // 반짝이는 배지. 유료 옵션 상품이라는 표시다
      },
    ],
  },
  { name: "assignment", text: "과제 미완수자 관리", subTabList: [] },
  { name: "students", text: "학생별 학습 내역", subTabList: [] },
  { name: "paper-assignment", text: "알림장 전송", subTabList: [] },
  {
    name: "academy",
    text: "학원 관리",
    subTabList: [
      { name: "students", text: "재원생 관리" },
      { name: "settings", text: "로고 및 카카오채널 관리" },
      { name: "notification", text: "알림톡 관리" },
      { name: "monthly", text: "월간 보고서" },
      { name: "support", text: "오류 신고" },
    ],
  },
];

// 문법뱅크. 지금은 검증 중이라 관리자에게만 보인다. 모두에게 열 때는 BASE_TAB_LIST 로
// 옮기면 된다(서버 차단은 `admin_name_list` 이므로 그때 서버 쪽도 함께 열어야 한다).
const GRAMMAR_BANK_TAB = { name: "grammar-bank", text: "문법뱅크", subTabList: [] };

const ADMIN_TAB = {
  name: "admin",
  text: "관리자",
  subTabList: [
    { name: "results", text: "레벨테스트 현황" },
    { name: "issue", text: "출제 현황" },
    { name: "partner", text: "협력학원 관리" },
    { name: "support", text: "오류 신고" },
  ],
};

// 메뉴를 지정하지 않고 들어왔을 때 보낼 곳. App.js, CallbackPage 도 이 값을 쓴다.
export const DEFAULT_TAB_PATH = "/worksheet/list";

const buildTabPath = (tab) => {
  const firstSubTabName = tab.subTabList[0]?.name;
  return firstSubTabName ? `/${tab.name}/${firstSubTabName}` : `/${tab.name}`;
};

const HomePage = () => {
  const { user, isUserLoaded } = useSelector((state) => state.auth);
  const { topTab, subTab } = useParams();

  // 진단 보고서 팝업을 먼저 보여 주고, 다음이 오류 수정 알림, 그것이 끝나야
  // 체험 안내가 뜬다. 겹쳐 뜨면 둘 다 안 읽고 닫는다.
  const [isSummaryResolved, setIsSummaryResolved] = useState(false);
  const handleSummaryResolved = useCallback(() => setIsSummaryResolved(true), []);
  const [isFixNoticeResolved, setIsFixNoticeResolved] = useState(false);
  const handleFixNoticeResolved = useCallback(
    () => setIsFixNoticeResolved(true),
    []
  );
  const navigate = useNavigate();
  const location = useLocation();

  // 로그인 없이 메뉴 주소로 바로 들어오면 로그인으로 보내되, **가려던 주소를 기억한다.**
  // 지인에듀 랜딩이 레벨테스트 화면으로 바로 링크를 걸기 때문에, 처음 들어오는 사람은
  // 반드시 이 경로를 지난다. 기억하지 않으면 로그인 뒤 문제지 목록에 떨어져서
  // 정작 보러 온 것을 못 본다.
  useEffect(() => {
    if (authService.isAuthenticated()) return;
    savePostLoginRedirect(location.pathname);
    navigate("/login", { replace: true });
  }, [location.pathname, navigate]);

  // 레벨테스트 화면을 어디까지 여는가. "full" | "light" | "none".
  // full  계약한 학원. 재원생·신규생·단어까지
  // light 계약은 안 했는데 코드를 쥔 학원. 코드함만 (LevelTestCodeVault)
  // none  보여 줄 것이 없어 탭을 감춘다
  //
  // null 은 '아직 모름' 이다. "none" 으로 시작하면 /worksheet/leveltest 에서
  // 새로고침할 때마다 문제지 목록으로 튕긴다(관리자 탭과 같은 이유).
  const [levelTestLevel, setLevelTestLevel] = useState(null);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      setLevelTestLevel("none");
      return undefined;
    }

    let isCurrent = true;
    fetchLevelTestEntitlementAPI()
      .then((data) => {
        if (isCurrent) {
          // 배포 중에 구 서버가 잠깐 공존하면 level 이 없을 수 있다.
          // 그때는 예전 값으로 판단한다.
          setLevelTestLevel(
            data.level || (data.can_use_leveltest ? "full" : "none")
          );
        }
      })
      .catch(() => {
        // 못 물어봤으면 감춘다. 값을 모를 때 열어 두면 안 되는 쪽이다.
        if (isCurrent) {
          setLevelTestLevel("none");
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  // 레벨테스트 탭은 계약 여부와 상관없이 보여 준다. 계약 전(none) 학원에게는
  // 관리 화면 대신 자동복습 안내(review-guide)를 보여 서비스를 소개한다.
  // 이 목록의 계정만 예외로 탭을 숨긴다 (2026-08-25 사용자 지시).
  const LEVELTEST_HIDDEN_NICKNAME_LIST = ["hillerypark"]; // 박윤서
  const tabList = useMemo(() => {
    const baseList =
      LEVELTEST_HIDDEN_NICKNAME_LIST.includes(user.nickname)
        ? BASE_TAB_LIST.map((tab) =>
            tab.name === "worksheet"
              ? {
                  ...tab,
                  subTabList: tab.subTabList.filter(
                    (subTabItem) => subTabItem.name !== "leveltest"
                  ),
                }
              : tab
          )
        : BASE_TAB_LIST;

    // 문법뱅크·출제기는 내부 전용이라 운영에 내보내지 않는다. 탭을 아예 빼서
    // 운영진에게도 숨긴다 (서버 라우트도 내려가 있다).
    return secret.ADMIN_NAME_LIST.includes(user.nickname)
      ? [...baseList, ADMIN_TAB]
      : baseList;
  }, [user.nickname, levelTestLevel]);

  // 주소를 손으로 고쳐 들어오는 경우가 있으므로 탭 이름을 메뉴 목록과 대조한다.
  // 관리자가 아닌 사람의 tabList 에는 admin 이 없어서 /admin 도 여기서 걸린다.
  const activeTab = tabList.find((tab) => tab.name === topTab);
  if (!activeTab) {
    // 사용자 정보를 받기 전에는 관리자인지 알 수 없다. 그때 판정해 버리면
    // /admin 에서 새로고침할 때마다 문제지 목록으로 튕긴다. 받을 때까지 기다린다.
    // 로그인 상태일 때만 기다린다. 아니면 받아올 사람이 없어 영영 안 끝난다.
    if (!isUserLoaded && authService.isAuthenticated()) {
      return null;
    }
    return <Navigate to={DEFAULT_TAB_PATH} replace />;
  }

  // 하위 메뉴가 있는데 주소에 없거나 엉뚱하면 첫 하위 메뉴로 보낸다.
  const hasValidSubTab = activeTab.subTabList.some((tab) => tab.name === subTab);
  if (activeTab.subTabList.length > 0 && !hasValidSubTab) {
    return <Navigate to={buildTabPath(activeTab)} replace />;
  }

  const selectTab = (tabName) => {
    const tab = tabList.find((item) => item.name === tabName);
    if (tab) {
      navigate(buildTabPath(tab));
    }
  };

  const selectSubTab = (subTabName) => {
    navigate(`/${activeTab.name}/${subTabName}`);
  };

  let content;
  if (topTab === "worksheet") {
    if (subTab === "list") {
      // 문제지 목록 화면
      content = <WorksheetTable />;
    } else if (subTab === "given") {
      // 출제 문제지 목록 탭
      content = <AssignedWorksheetTable />;
    } else if (subTab === "bank") {
      // 문제뱅크(beta): 뱅크에서 범위를 골라 시험지를 만들고 보통 과제로 출제한다
      content = <ProblemBankPage />;
    } else if (subTab === "retry") {
      // 복습 문제지 목록 탭
      content = <AssignedRetryWorksheetTable />;
    } else if (subTab === "leveltest") {
      // 계약한 학원은 관리 화면(재원생 / 신규생 / 단어리스트), 계약 안 한 학원은
      // 코드함만. 실제 차단은 서버가 하고 여기는 무엇을 그릴지만 고른다.
      content =
        levelTestLevel === "light" ? (
          <LevelTestCodeVault />
        ) : levelTestLevel === "none" ? (
          // 계약 전 학원: 관리할 것이 없으니 자동복습 안내로 서비스를 소개한다.
          // 정적 페이지를 그대로 끼워 넣는다 (원장에게 보내는 안내와 같은 판).
          <iframe
            src="/teacher/review-guide.html"
            title="ECHO 자동복습 안내"
            style={{
              width: "100%",
              height: "calc(100vh - 140px)",
              border: "none",
              display: "block",
            }}
          />
        ) : (
          <LevelTestManagement />
        );
    }
  } else if (topTab === "assignment") {
    // 과제 제출
    content = <UnfinishedWorksheetTable />;
  } else if (topTab === "paper-assignment") {
    // 지필 과제 관리
    content = <PaperAssignmentPage />;
  } else if (topTab === "students") {
    // 과제 제출
    content = <AssignmentReviewTable />;
  } else if (topTab === "academy") {
    if (subTab === "students") {
      content = <StudentManagementPage />;
    } else if (subTab === "notification") {
      // [메뉴 정리] 알림톡 관리를 학원 관리 하위로 이동
      content = <NotificationManagementPage />;
    } else if (subTab === "monthly") {
      content = <MonthlyReportPage />;
    } else if (subTab === "support") {
      content = <ErrorReportPage />;
    } else {
      content = <AcademyManagementPage />;
    }
  } else if (topTab === "grammar-bank") {
    content = <GrammarBankPage />;
  } else if (topTab === "admin") {
    if (subTab === "support") {
      // 전체 학원의 오류 신고. 여기서 적은 답변이 학원 화면에 그대로 나간다.
      content = <AdminErrorReportPage />;
    } else if (subTab === "issue") {
      // 학원별 출제 내역과 월간 결산
      content = <AdminPage />;
    } else if (subTab === "partner") {
      // 랜딩 지도에 올릴 협력학원과 그 교사 계정을 잇는다
      content = <PartnerAcademyPage />;
    } else {
      // 전체 레벨테스트 현황 (응시, 응시코드, 재원생)
      content = <AdminLevelTestPage />;
    }
  } else {
    content = (
      <div style={{ padding: "20px" }}>
        <h1>준비 중</h1>

        <div>아직 내용이 없습니다.</div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'Pretendard', 'Spoqa Han Sans Neo', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
        backgroundColor: "#F5F8F8",
        letterSpacing: "-0.01em",
        minHeight: "100vh",
      }}
    >
      <TopNavBar
        topTab={topTab}
        subTab={subTab}
        selectTab={selectTab}
        selectSubTab={selectSubTab}
        tabList={tabList}
      />
      {content}
      {/* 달마다 한 번, 지난달 진단 보고서를 먼저 알린다. 닫으면 그 달에는 다시 안 뜬다.
          이것을 처리하기 전에는 아래 체험 안내를 띄우지 않는다. 두 개가 겹쳐 뜨면
          둘 다 안 읽고 닫는다 */}
      <MonthlySummaryPopup onResolved={handleSummaryResolved} />
      {/* 결산은 앱 안에서 띄운다. 아이폰이 blob 주소의 새 탭을 막는다 */}
      <MonthlySummaryViewer />
      {/* 등록 학생이 정원을 넘으면 알린다. 넘긴 학생은 화면에서 사라져 있어서
          알려 주지 않으면 아이가 지워진 줄 안다 */}
      {isSummaryResolved && <CapacityOverPopup />}
      {/* 신고한 오류를 고쳤으면 알린다. 신고한 학원에게만, 한 번만 뜬다 */}
      {isSummaryResolved && (
        <FixNoticePopup onResolved={handleFixNoticeResolved} />
      )}
      {/* 단어만 쓰기로 한 학원에는 레벨 체험 팝업을 주지 않는다 (2026-08-24) */}
      {isSummaryResolved && isFixNoticeResolved && user.nickname !== "hillerypark" && (
        <ExperiencePromoModal nickname={user.nickname} />
      )}
    </div>
  );
};

export default HomePage;
